/**
 * Swimlane board: columns are workflow document steps, never a hardcoded hop list.
 */
import {
  PIPELINE_STEP_KINDS,
  REGISTERED_TOOL_IDS,
  addPipelineStep,
  defaultPipelineWorkflow,
  isRegisteredToolId,
  movePipelineStep,
  movePipelineTool,
  parsePipelineWorkflow,
  removePipelineStep,
  parsePipelineWorkflowRecord,
  pipelineStepLaneAvailability,
  pipelineToolAvailability,
  pipelineToolTitle,
  pipelineBoardLaneIds,
  pipelineBoardSteps,
  pipelineWorkflowSaveFailureMessage,
  pipelineWorkflowSaveResultFromHttp,
  type PipelineMissBehavior,
  type PipelineStep,
  type PipelineStepKind,
  type PipelineWorkflowDocument,
  type PipelineWorkflowSaveResult
} from "../../../packages/core/src/pipeline-workflow.js";
import {
  PIPELINE_PILE_MARK,
  pipelinePileKindOrAlert,
  treesPipelineLaneHeaderCount,
  treesPipelineLaneWork,
  treesPipelinePopulation,
  treesPipelineSourceCompleteness,
  type PipelinePileKind,
  type TreesPipelineLaneWork,
  type TreesPipelinePopulation
} from "../../../packages/core/src/trees-pipeline-summary.js";
import {
  TREE_INGEST_LABELS,
  TREE_INGEST_ORDER,
  ingestTreeStatuses,
  pipelineAttentionAlerts,
  pipelineAttentionHtml
} from "./dashboard-pipeline-alerts.js";
import { pipelineToolStateChipClass, pipelineToolStateLabel } from "../../../packages/core/src/pipeline-tool-status.js";
import { escapeAttr, escapeHtml, labelize } from "./dashboard-html.js";
import {
  ecourtsMonitorHtml,
  ecourtsMonitorOpenFromStorage,
  persistEcourtsMonitorOpen
} from "./dashboard-ecourts-monitor.js";
import { bindNewTicketControl, newTicketButtonHtml } from "./dashboard-new-ticket.js";
import {
  bindPipelineRunInspector,
  emptyPipelineRunInspectorState,
  pipelineRunInspectorHtml,
  startPipelineRunInspectorPoll,
  type PipelineRunInspectorState
} from "./dashboard-pipeline-run-inspector.js";
import { formatInteger } from "./format.js";
import type { PropertiesConveyorHealth } from "./dashboard-command-center.js";
import {
  TREES_PIPELINE_DEADEND_COPY,
  treesPipelineListHref
} from "./dashboard-trees-pipeline.js";

const PIPELINE_WORKFLOW_PATH = "/dashboard/pipeline-workflow";
const PIPELINE_WORKFLOW_PREVIEW_PATH = "/dashboard/pipeline-workflow/preview";

export { pipelineBoardLaneIds, pipelineBoardSteps } from "../../../packages/core/src/pipeline-workflow.js";

const STEP_KINDS: readonly PipelineStepKind[] = PIPELINE_STEP_KINDS;
const MISS_LABELS: Record<PipelineMissBehavior, string> = {
  block: "Block the house",
  hold: "Hold in this lane"
};

/** Conveyor stage ids → workflow step ids when the health payload still uses hopper names. */
const CONVEYOR_STAGE_ALIASES: Record<string, string> = {
  need_owner: "owner",
  need_skip: "skip",
  need_value: "value",
  need_plv: "plv",
  need_equity: "equity",
  need_close: "close",
  need_obit: "obit",
  ready_to_call: "ready", // confirmed Close delivery only; pending status_ready stays on close
  on_hold: "hold",
  blocked: "blocked"
};

export function pipelineBoardRequested(pathname?: string, search?: string): boolean {
  const path = (pathname ?? (typeof window === "undefined" ? "/" : window.location.pathname)).replace(/\/+$/, "") || "/";
  if (path === "/workflow" || path === "/trees-pipeline") return true;
  if (path !== "/") return false;
  const query = search ?? (typeof window === "undefined" ? "" : window.location.search);
  return !new URLSearchParams(query).get("raise_package");
}

export const PIPELINE_BOARD_POLL_MS = 2_000;

export async function renderPipelineBoard(mount: HTMLElement): Promise<void> {
  const { fetchPropertiesConveyorHealth } = await import("./dashboard-properties-conveyor.js");
  const loaded = await loadPipelineWorkflow();
  const health = await fetchPropertiesConveyorHealth().catch(() => null);
  const state: BoardState = {
    workflow: loaded.document,
    revision: loaded.revision,
    workflowError: loaded.error,
    saving: false,
    health,
    selectedId: loaded.document.steps[0]?.id ?? "",
    drawerOpen: false,
    sheetOpen: false,
    workflowPreview: null,
    laneScrollLeft: 0,
    pageScrollX: 0,
    pageScrollY: 0,
    ...emptyPipelineRunInspectorState()
  };
  paintPipelineBoard(mount, state);
  startPipelineBoardPoll(mount, state);
  startPipelineRunInspectorPoll(mount, state, (patch) => {
    Object.assign(state, patch);
    paintPipelineBoard(mount, state);
  });
}

function startPipelineBoardPoll(mount: HTMLElement, state: BoardState): void {
  let inflight = false;
  let id = 0;
  const tick = (): void => {
    void (async () => {
      if (!mount.querySelector("[data-pipeline-board]")) {
        window.clearInterval(id);
        return;
      }
      if (typeof document !== "undefined" && document.hidden) return;
      if (state.sheetOpen || state.inspectOpen) return;
      if (mount.querySelector("input:focus, textarea:focus, select:focus")) return;
      if (inflight) return;
      inflight = true;
      try {
        const { fetchPropertiesConveyorHealth } = await import("./dashboard-properties-conveyor.js");
        const health = await fetchPropertiesConveyorHealth().catch(() => null);
        if (!health || !mount.querySelector("[data-pipeline-board]")) {
          if (!health) refreshEcourtsMonitor(mount, null);
          return;
        }
        rememberPipelineBoardScroll(mount, state);
        const sameCounts = pipelineBoardPollKey(state.workflow, health) === pipelineBoardPollKey(state.workflow, state.health);
        state.health = health;
        if (sameCounts) {
          refreshPipelineBoardLiveChrome(mount, health);
          restorePipelineBoardScroll(mount, state);
          return;
        }
        paintPipelineBoard(mount, state);
      } finally {
        inflight = false;
      }
    })();
  };
  tick();
  id = window.setInterval(tick, PIPELINE_BOARD_POLL_MS);
}

type BoardState = {
  workflow: PipelineWorkflowDocument;
  revision: number;
  workflowError: Extract<PipelineWorkflowSaveResult, { ok: false }> | null;
  saving: boolean;
  health: PropertiesConveyorHealth | null;
  selectedId: string;
  drawerOpen: boolean;
  sheetOpen: boolean;
  workflowPreview: PipelineWorkflowPublishPreview | null;
  laneScrollLeft: number;
  pageScrollX: number;
  pageScrollY: number;
} & PipelineRunInspectorState;

export type PipelineWorkflowPublishPreview = {
  identityKeys: string[];
  count: number;
};

function pipelineBoardPollKey(workflow: PipelineWorkflowDocument, health: PropertiesConveyorHealth | null): string {
  return JSON.stringify({
    identity: pipelineBoardCountIdentity(workflow, health),
    work: (health?.tree?.stages ?? []).map((stage) => [
      stage.id,
      stage.work?.eligible,
      stage.work?.executing,
      stage.work?.throughput,
      stage.work?.nextWakeupAt,
      stage.work?.lastVerifiedSuccessAt
    ]),
    attention: pipelineAttentionAlerts(health),
    tools: (health?.tools ?? []).map((tool) => [tool.toolId, tool.state, tool.failureClass]),
    workflowFailureClass: health?.workflowFailureClass ?? null
  });
}

export function rememberPipelineBoardScroll(mount: ParentNode, state: Pick<BoardState, "laneScrollLeft" | "pageScrollX" | "pageScrollY">): void {
  const lanes = mount.querySelector("[data-pipeline-lanes]");
  if (lanes) state.laneScrollLeft = lanes.scrollLeft;
  if (typeof window === "undefined") return;
  state.pageScrollX = window.scrollX;
  state.pageScrollY = window.scrollY;
}

export function restorePipelineBoardScroll(mount: ParentNode, state: Pick<BoardState, "laneScrollLeft" | "pageScrollX" | "pageScrollY">): void {
  const lanes = mount.querySelector("[data-pipeline-lanes]");
  if (lanes) lanes.scrollLeft = state.laneScrollLeft;
  if (typeof window === "undefined") return;
  window.scrollTo(state.pageScrollX, state.pageScrollY);
}

function refreshPipelineBoardLiveChrome(mount: HTMLElement, health: PropertiesConveyorHealth | null): void {
  const attention = pipelineAttentionAlerts(health);
  const status = mount.querySelector(".pipeline-status");
  if (status) status.innerHTML = `${pipelinePopulationHtml(health)}${healthBadge(health, attention.length > 0)}`;
  const copy = mount.querySelector("[data-so2-live]");
  if (copy) copy.textContent = pipelineToolbarCopy(health);
  refreshEcourtsMonitor(mount, health?.ecourts ?? null);
}

function refreshEcourtsMonitor(mount: HTMLElement, health: PropertiesConveyorHealth["ecourts"] | null): void {
  const monitor = mount.querySelector("[data-ecourts-monitor]");
  const open = monitor instanceof HTMLDetailsElement ? monitor.open : ecourtsMonitorOpenFromStorage();
  if (monitor) monitor.outerHTML = ecourtsMonitorHtml(health, { open });
  bindEcourtsMonitor(mount);
}

export function pipelineBoardHtml(
  workflow: PipelineWorkflowDocument,
  health: PropertiesConveyorHealth | null,
  selectedId: string,
  options: {
    drawerOpen?: boolean;
    sheetOpen?: boolean;
    workflowError?: Extract<PipelineWorkflowSaveResult, { ok: false }> | null;
    workflowPreview?: PipelineWorkflowPublishPreview | null;
  } = {}
): string {
  const parsed = parsePipelineWorkflow(workflow);
  const visibleSteps = pipelineBoardSteps(parsed);
  const selected = parsed.steps.find((step) => step.id === selectedId) ?? visibleSteps[0] ?? parsed.steps[0] ?? null;
  const lanes = visibleSteps
    .map((step) =>
      laneHtml(
        step,
        laneCount(step, health),
        Boolean(selected && step.id === selected.id),
        stageForStep(step.id, health),
        health?.tools,
        {
          work: health?.tree ? treesPipelineLaneWork(step.id, health.tree) : null,
          completeness: step.id === "ingest" ? ingestCompletenessLabel(health) : null,
          ingestTrees: step.id === "ingest" ? ingestTreeStatuses(health, ingestAsOf(health)) : null,
          ingestRuntime: step.id === "ingest" ? health?.ingest ?? null : null,
          ecourts: step.id === "auction_window" ? health?.ecourts ?? null : null
        }
      )
    )
    .join("");
  const drawerOpen = options.drawerOpen !== false && Boolean(selected);
  const sheetOpen = Boolean(options.sheetOpen);
  const attention = pipelineAttentionAlerts(health);
  const workflowError = options.workflowError ?? workflowErrorFromHealth(health);
  return `
    <div class="pipeline-shell" data-pipeline-board>
      <header class="pipeline-top">
        <div class="wordmark">Pipeline<small>seven trees → Close</small></div>
        <nav aria-label="pipeline views">
          <a href="/workflow" class="is-on">Workflow</a>
          <a href="/ingest">Ingest</a>
          <a href="/tools">Tools</a>
          <a href="/?raise_package=1">Raise package</a>
        </nav>
        <div class="pipeline-status">${pipelinePopulationHtml(health)}${healthBadge(health, attention.length > 0)}</div>
      </header>
      ${pipelineAttentionHtml(attention)}
      ${pipelineWorkflowErrorHtml(workflowError)}
      ${ecourtsMonitorHtml(health?.ecourts, { open: ecourtsMonitorOpenFromStorage() })}
      <div class="pipeline-toolbar">
        <div>
          <h1>Seven trees → Close</h1>
          <p data-so2-live>${pipelineToolbarCopy(health)}</p>
        </div>
        <div class="pipeline-toolbar-grow"></div>
        ${newTicketButtonHtml()}
        <button type="button" class="btn" data-run-inspect>Inspect run</button>
        <button type="button" class="btn" data-pipeline-edit ${selected ? "" : "disabled"}>Edit step</button>
        <button type="button" class="btn main" data-pipeline-add>Add step</button>
      </div>
      <div class="pipeline-board-wrap" data-pipeline-lanes>
        <div class="pipeline-board">
          ${lanes}
          <button type="button" class="pipeline-add-lane" data-pipeline-add>+ Add step</button>
        </div>
      </div>
      ${selected ? inspectorHtml(selected, parsed, drawerOpen, health, options.workflowPreview) : ""}
      ${addStepSheetHtml(parsed, sheetOpen)}
    </div>
  `;
}

export function laneHtml(
  step: PipelineStep,
  count: string,
  selected: boolean,
  stage: { id: string; ready: number; sick?: string | null; running?: boolean; deadends: Array<{ class: string; count: number }>; work?: TreesPipelineLaneWork | null } | null = null,
  tools: NonNullable<PropertiesConveyorHealth["tools"]> | undefined = undefined,
  detail: {
    work?: TreesPipelineLaneWork | null;
    completeness?: string | null;
    ingestTrees?: ReturnType<typeof ingestTreeStatuses> | null;
    ingestRuntime?: PropertiesConveyorHealth["ingest"] | null;
    ecourts?: PropertiesConveyorHealth["ecourts"] | null;
  } = {}
): string {
  const chips = stepChips(step, tools)
    .map((chip) => `<span class="chip${chip.kind ? ` ${escapeAttr(chip.kind)}` : ""}">${escapeHtml(chip.text)}</span>`)
    .join("");
  const cards = stepCards(step, tools)
    .map((card) => `<article class="pipeline-card"><b>${escapeHtml(card.title)}</b><span>${escapeHtml(card.note)}</span></article>`)
    .join("");
  const readyHref = stage && count !== "unavailable" && stage.id !== "ingest" ? treesPipelineListHref(stage.id) : null;
  const readyKind = stage && count !== "unavailable" && stage.id !== "ingest" && stage.ready > 0
    ? (stage.sick ? "sick" : stage.running ? "running" : "ok")
    : "";
  const readyMark = readyKind ? laneReadyMark(readyKind === "sick") : "";
  const sickChip = !tools?.length && stage?.sick
    ? `<span class="chip wait">${escapeHtml(laneSickChipLabel(stage.sick))}</span>`
    : "";
  const countHtml = readyHref
    ? `<a class="pipeline-count" data-stage-ready${readyKind ? ` data-ready-mark="${escapeAttr(readyKind)}"` : ""} href="${escapeAttr(readyHref)}" target="_blank" rel="noopener">${readyMark}${escapeHtml(count)}</a>`
    : `<div class="pipeline-count">${readyMark}${escapeHtml(count)}</div>`;
  const deadends = (stage?.deadends ?? []).filter((row) => row.count > 0);
  const work = detail.work ?? null;
  const deadendHtml = deadends.length
    ? `<ul class="pipeline-deadends">${deadends
        .map((row) => {
          const kind = pipelinePileKindOrAlert(row.class);
          const wakeup = (row.class === "over_30_days" || row.class === "past_or_unknown") && work?.nextWakeupAt
            ? ` Next wakeup ${work.nextWakeupAt}.`
            : "";
          return `<li data-deadend="${escapeAttr(row.class)}" data-pile="${escapeAttr(kind)}"><a href="${escapeAttr(stage!.id === "ingest" ? "/ingest" : treesPipelineListHref(stage!.id, row.class))}" target="_blank" rel="noopener"><b>${pileMark(kind)} ${formatInteger(row.count)}</b></a> ${escapeHtml(deadendCopy(row.class))}${escapeHtml(wakeup)}</li>`;
        })
        .join("")}</ul>`
    : "";
  const laneState = pipelineStepLaneAvailability(step, tools);
  const unavailable = laneState === "unavailable" || laneState === "disabled";
  const workHtml = unavailable
    ? ""
    : step.id === "ingest"
      ? ingestWorkHtml(detail.ingestTrees ?? [], detail.completeness ?? null, detail.ingestRuntime ?? null)
      : `${laneWorkHtml(work, detail.completeness ?? null)}${step.id === "auction_window" ? ecourtsSubcountsHtml(detail.ecourts) : ""}`;
  return `
    <section class="pipeline-lane${selected ? " is-pick" : ""}${unavailable ? " is-unavailable" : ""}" data-pipeline-step="${escapeAttr(step.id)}"${unavailable ? ` data-lane-availability="${escapeAttr(laneState)}"` : ""}>
      <header class="pipeline-lane-h">
        <h2>${escapeHtml(step.title)}</h2>
        ${countHtml}
        <div class="pipeline-chips">${chips}${sickChip}</div>
      </header>
      <div class="pipeline-stack">${cards}${workHtml}${deadendHtml}</div>
    </section>
  `;
}

function ecourtsSubcountsHtml(health: PropertiesConveyorHealth["ecourts"] | null | undefined): string {
  const counts = health?.subcounts;
  const value = (count: number | undefined): string => count == null ? "unavailable" : formatInteger(count);
  const leftover: Array<[string, string, number | undefined]> = [
    ["incomplete-retrieval", "Incomplete retrieval — retry fetch", counts?.incompleteRetrieval],
    ["uncertain-facts", "Uncertain facts — re-fetch", counts?.uncertainFacts],
    ["retryable-service", "Retryable service failure", counts?.retryableService],
    ["terminal-infra", "Terminal lookup/extension failure", counts?.terminalInfra],
    ["house-miss", "House miss — retry on cadence", counts?.houseMiss],
    ["upsert-pending", "Upsert pending (no stored case yet)", counts?.upsertPending],
    ["analysis-leak", "Analysis PLV class — wrong hop", counts?.analysisLeak],
    ["pending-no-job", "Pending, no live retrieval job", counts?.pendingNoJob],
    ["never-linked", "Never linked to a case", counts?.neverLinked],
    ["other-ecourts", "Other eCourts class", counts?.otherEcourts]
  ];
  const leftoverHtml = leftover
    .filter(([, , count]) => count != null)
    .map(([work, label, count]) => `<div data-work="${work}"><dt>${label}</dt><dd>${escapeHtml(value(count))}</dd></div>`)
    .join("");
  return `
    <div class="ecourts-subcounts" data-ecourts-subcounts>
      <p class="pipeline-work-note">Handoff. Upsert pending is a clean stored case. Leftover rows are exclusive.</p>
      <dl class="ecourts-subcounts-list">
        <div data-work="retrieval-pending"><dt>Waiting on eCourts retrieval</dt><dd>${escapeHtml(value(counts?.retrievalPending))}</dd></div>
        <div data-work="case-stored-awaiting-property-upsert"><dt>Case stored, ready to upsert</dt><dd>${escapeHtml(value(counts?.caseStoredAwaitingPropertyUpsert))}</dd></div>
        <div data-work="upsert-complete"><dt>Property upsert complete</dt><dd>${escapeHtml(value(counts?.upsertComplete))}</dd></div>
        ${leftoverHtml}
      </dl>
    </div>
  `;
}

export function stageForStep(
  stepId: string,
  health: PropertiesConveyorHealth | null
): { id: string; ready: number; sick?: string | null; running?: boolean; deadends: Array<{ class: string; count: number }> } | null {
  if (stepId === "ingest") return ingestStuckStage(health);
  const stage = stageRow(stepId, health);
  if (stage) {
    return {
      id: stage.id,
      ready: treesPipelineLaneHeaderCount(stage),
      sick: stage.sick ?? null,
      running: Boolean(stage.running),
      deadends: deadendsForLane(stepId, stage.deadends ?? [])
    };
  }
  if (stepId !== "plv" && stepId !== "equity" && stepId !== "intestacy") return null;
  const host = stageRow(stepId === "intestacy" ? "obit" : "value", health);
  if (!host) return null;
  return { id: host.id, ready: 0, deadends: deadendsForLane(stepId, host.deadends ?? []) };
}

function stageRow(stepId: string, health: PropertiesConveyorHealth | null) {
  const tree = health?.tree;
  if (!tree) return null;
  const exact = tree.stages.find((row) => row.id === stepId);
  if (exact) return exact;
  const alias = Object.entries(CONVEYOR_STAGE_ALIASES).find(([, laneId]) => laneId === stepId)?.[0];
  if (!alias) return null;
  return tree.stages.find((row) => row.id === alias) ?? null;
}

export function pipelineBoardPopulation(health: PropertiesConveyorHealth | null): TreesPipelinePopulation | null {
  const tree = health?.tree;
  if (!tree) return null;
  return treesPipelinePopulation(tree);
}

export type PipelineBoardCountIdentity = {
  trees: number;
  headerReady: number;
  headerHeld: number;
  headerAlert: number;
  headerStop: number;
  gap: number;
  laneReady: number;
  lanePiles: number;
  closed: boolean;
};

/** Lane ready + lane-detail piles (excluding ingest) vs page-header trees split. */
export function pipelineBoardCountIdentity(
  workflow: PipelineWorkflowDocument,
  health: PropertiesConveyorHealth | null
): PipelineBoardCountIdentity {
  const header = treesPipelinePopulation(health?.tree ?? { properties: 0, stages: [] });
  let laneReady = 0;
  let lanePiles = 0;
  for (const step of parsePipelineWorkflow(workflow).steps) {
    if (step.id === "ingest") continue;
    const stage = stageForStep(step.id, health);
    if (!stage) continue;
    laneReady += stage.ready;
    lanePiles += stage.deadends.reduce((sum, row) => sum + row.count, 0);
  }
  const headerSplit = header.ready + header.held + header.alert + header.stop;
  const closed =
    header.gap === 0
    && headerSplit === header.properties
    && laneReady === header.ready
    && laneReady + lanePiles === header.properties;
  return {
    trees: header.properties,
    headerReady: header.ready,
    headerHeld: header.held,
    headerAlert: header.alert,
    headerStop: header.stop,
    gap: header.gap,
    laneReady,
    lanePiles,
    closed
  };
}

function pipelinePopulationHtml(health: PropertiesConveyorHealth | null): string {
  const pop = pipelineBoardPopulation(health);
  if (!pop) return "";
  const gap = pop.gap === 0
    ? ""
    : ` <span class="pipeline-pop-gap" data-tree-gap>${formatInteger(Math.abs(pop.gap))} ${pop.gap > 0 ? "uncounted" : "extra"}</span>`;
  return `<p class="pipeline-pop" data-pipeline-pop>
      <b data-tree-properties>${formatInteger(pop.properties)}</b> trees
      <span class="pipeline-pop-split">
        <span data-tree-ready title="Ready to process">${PIPELINE_PILE_MARK.readyOk} <b>${formatInteger(pop.ready)}</b> ready</span>
        <span data-tree-held title="Held by design">${PIPELINE_PILE_MARK.held} <b>${formatInteger(pop.held)}</b> held</span>
        <span data-tree-alert title="Needs a fix">${PIPELINE_PILE_MARK.alert} <b>${formatInteger(pop.alert)}</b> need a fix</span>
        <span data-tree-stop title="Cannot go forward">${PIPELINE_PILE_MARK.stop} <b>${formatInteger(pop.stop)}</b> cannot go forward</span>
      </span>${gap}
    </p>`;
}

function pipelineToolbarCopy(health: PropertiesConveyorHealth | null): string {
  const pop = pipelineBoardPopulation(health);
  if (!pop) return "Swimlanes · ready counts · subtotals. Each column is a workflow step.";
  if (pop.gap !== 0) {
    return "Swimlanes · ready + held + need-a-fix + cannot-go-forward should equal trees.";
  }
  return "Swimlanes · eligible vs completed · 🟢/🟡 ready · ⏳ held · 🚨 need a fix · 🛑 cannot go forward. Ingest is tree freshness, not houses. Completeness is separate.";
}

function ingestAsOf(health: PropertiesConveyorHealth | null): Date {
  const generated = health?.generatedAt ? Date.parse(health.generatedAt) : NaN;
  return Number.isFinite(generated) ? new Date(generated) : new Date();
}

function ingestCompletenessLabel(health: PropertiesConveyorHealth | null): string | null {
  if (!health?.tree) return null;
  const completeness = treesPipelineSourceCompleteness(health.tree.sources ?? [], TREE_INGEST_ORDER);
  return `completeness ${completeness.present}/${completeness.expected}`;
}

function ingestWorkHtml(
  trees: ReturnType<typeof ingestTreeStatuses>,
  completeness: string | null,
  ingest: PropertiesConveyorHealth["ingest"] | null = null
): string {
  if (!trees.length && !completeness && !ingest) return "";
  const fresh = trees.filter((row) => !row.stale && !row.missing).length;
  const stale = trees.filter((row) => row.stale && !row.missing).length;
  const missing = trees.filter((row) => row.missing).length;
  const rows = trees.length
    ? [
        ["fresh trees", fresh],
        ["stale trees", stale],
        ["missing trees", missing]
      ]
    : [];
  const clocks = [
    ingest?.enabled === false ? "ingest flag off" : ingest?.enabled === true ? "ingest enabled" : null,
    ingest?.lastTickAt ? `scheduler ${ingest.lastTickAt}` : null,
    ingest?.lastAttemptAt ? `last attempt ${ingest.lastAttemptAt}` : null,
    ingest?.lastFailureClass ? ingest.lastFailureClass : null
  ].filter(Boolean);
  return `
    <dl class="pipeline-work" data-lane-work>
      ${rows.map(([label, value]) => `<div data-work="${escapeAttr(String(label))}"><dt>${escapeHtml(String(label))}</dt><dd>${escapeHtml(formatInteger(value as number))}</dd></div>`).join("")}
      ${completeness ? `<div data-work="completeness"><dt>source completeness</dt><dd>${escapeHtml(completeness)}</dd></div>` : ""}
    </dl>
    ${clocks.length ? `<p class="pipeline-work-clocks">${escapeHtml(clocks.join(" · "))}</p>` : ""}
  `;
}

function workCountLabel(value: number | null | undefined): string {
  return value == null ? "unavailable" : formatInteger(value);
}

function laneWorkHtml(work: TreesPipelineLaneWork | null, completeness: string | null): string {
  if (!work && !completeness) return "";
  const rows = work
    ? [
        ["eligible", work.eligible],
        ["executing", work.executing],
        ["scheduled wait", work.scheduledWait],
        ["provider wait", work.providerWait],
        ["rejected", work.rejected],
        ["completed", work.completed]
      ]
    : [];
  const clocks = work
    ? [
        work.throughput != null ? `throughput ${formatInteger(work.throughput)}` : "throughput unavailable",
        work.oldestEligibleAt ? `oldest eligible ${work.oldestEligibleAt}` : null,
        work.lastVerifiedSuccessAt ? `last success ${work.lastVerifiedSuccessAt}` : "last success unavailable",
        work.nextRetryAt ? `next retry ${work.nextRetryAt}` : null,
        work.nextWakeupAt ? `next wakeup ${work.nextWakeupAt}` : null
      ].filter(Boolean)
    : [];
  return `
    <dl class="pipeline-work" data-lane-work>
      ${rows.map(([label, value]) => `<div data-work="${escapeAttr(String(label))}"><dt>${escapeHtml(String(label))}</dt><dd>${escapeHtml(workCountLabel(value as number | null))}</dd></div>`).join("")}
      ${completeness ? `<div data-work="completeness"><dt>source completeness</dt><dd>${escapeHtml(completeness)}</dd></div>` : ""}
    </dl>
    ${clocks.length ? `<p class="pipeline-work-clocks">${escapeHtml(clocks.join(" · "))}</p>` : ""}
  `;
}

function laneRulesHtml(
  step: PipelineStep,
  workflow: PipelineWorkflowDocument,
  work: TreesPipelineLaneWork | null,
  health: PropertiesConveyorHealth | null
): string {
  const index = workflow.steps.findIndex((row) => row.id === step.id);
  const prev = index > 0 ? workflow.steps[index - 1] : null;
  const enter = prev ? `After ${prev.title}` : "Pipeline start";
  const exitBits: string[] = [];
  if (step.gate?.auctionWindowDays) exitBits.push(`auction within ${step.gate.auctionWindowDays} days`);
  if (step.tools[0]) exitBits.push(`${pipelineToolTitle(step.tools[0])} lands`);
  if (step.kind === "terminal") exitBits.push("terminal sink");
  if (step.miss === "hold") exitBits.push("miss holds here");
  else exitBits.push("miss blocks the house");
  const wait = laneWaitCopy(step, work, health);
  const stage = stageForStep(step.id, health);
  const housesHref = stage && stage.id !== "ingest" ? treesPipelineListHref(stage.id) : null;
  return `
    <div class="pipeline-rules" data-lane-rules>
      <p data-rule="enter"><strong>Enter</strong> ${escapeHtml(enter)}</p>
      <p data-rule="exit"><strong>Exit</strong> ${escapeHtml(exitBits.join(" · "))}</p>
      <p data-rule="wait"><strong>Waiting</strong> ${escapeHtml(wait)}</p>
      <p class="pipeline-rule-links">
        <a href="/tools" data-run-explanations>Tool runs</a>
        ${housesHref ? `<a href="${escapeAttr(housesHref)}" target="_blank" rel="noopener">Houses in this lane</a>` : `<a href="/ingest">Ingest freshness</a>`}
      </p>
    </div>
  `;
}

function laneWaitCopy(
  step: PipelineStep,
  work: TreesPipelineLaneWork | null,
  health: PropertiesConveyorHealth | null = null
): string {
  const tools = health?.tools;
  const lane = pipelineStepLaneAvailability(step, tools);
  if (lane === "unavailable" || lane === "disabled") {
    return lane === "disabled"
      ? "This lane is disabled. It is not an empty healthy queue."
      : "This lane is unavailable. It is not an empty healthy queue.";
  }
  if (!work) return "Counts unavailable until VPS health loads.";
  const sick = stageForStep(step.id, health)?.sick ?? null;
  const primary = step.tools[0];
  const status = tools?.find((row) => row.toolId === primary);
  const paused = Boolean(sick)
    || status?.state === "paused"
    || status?.state === "not_configured";
  const pauseClass = sick || status?.failureClass || status?.state || null;
  const autoRun = !paused && primary != null && pipelineToolAvailability(primary, status) === "executable";
  if (work.scheduledWait > 0) {
    if (work.nextWakeupAt) {
      return autoRun
        ? `Held by design until ${work.nextWakeupAt}. Then the next registered tool runs automatically.`
        : `Held by design until ${work.nextWakeupAt}.`;
    }
    return "Held by design. Waiting for the next recorded check.";
  }
  if (paused) {
    return pauseClass
      ? `This hop is paused (${pauseClass}). It will not drain until that class clears.`
      : "This hop is paused. It will not drain until that class clears.";
  }
  if (work.providerWait > 0) return "Waiting on a provider. The house is not failed.";
  if (work.executing != null && work.executing > 0) return "A tool is executing this hop now.";
  if (work.eligible > 0) {
    return autoRun
      ? "Eligible now. The next registered tool runs automatically."
      : "Eligible now. The hop is not executable.";
  }
  if (work.rejected > 0) return "Rejected or cannot go forward. Open the pile for the typed reason.";
  if (work.completed > 0 && work.eligible === 0) return "No houses waiting here. Completed count is later hops, not this queue.";
  return "No houses waiting in this lane.";
}

function laneSickChipLabel(sick: string): string {
  if (sick.startsWith("attom_owner")) return "ATTOM paused";
  if (sick.startsWith("reportall")) return "ReportAll paused";
  if (sick.startsWith("smartskip")) return "SmartSkip paused";
  if (/rate_limited/.test(sick)) return "rate limited";
  if (sick.startsWith("obituary")) return "Obituary paused";
  if (sick.startsWith("close_")) return "Close paused";
  if (sick === "conveyor_drain_stale") return "drain stalled";
  return "tool paused";
}

function laneReadyMark(sick: boolean): string {
  const mark = sick ? PIPELINE_PILE_MARK.readySick : PIPELINE_PILE_MARK.readyOk;
  return `<span class="pipeline-mark" aria-hidden="true">${mark}</span>`;
}

function pileMark(kind: PipelinePileKind): string {
  return PIPELINE_PILE_MARK[kind];
}

export function laneCount(step: PipelineStep, health: PropertiesConveyorHealth | null): string {
  const lane = pipelineStepLaneAvailability(step, health?.tools);
  if (lane === "unavailable" || lane === "disabled") return "unavailable";
  return countForStep(step.id, health);
}

export function countForStep(stepId: string, health: PropertiesConveyorHealth | null): string {
  if (!health?.tree) return "unavailable";
  const tree = health.tree;
  if (stepId === "ingest") {
    const sources = tree.sources ?? [];
    if (sources.length === 0) return "unavailable";
    const latest = tree.latestScrapeAt?.slice(0, 10);
    const bySource = new Map(sources.map((row) => [row.source, row]));
    const live = TREE_INGEST_ORDER.filter((id) => bySource.has(id));
    const denominator = live.length || TREE_INGEST_ORDER.length;
    const fresh = latest
      ? TREE_INGEST_ORDER.filter((id) => bySource.get(id)?.lastSeenAt?.slice(0, 10) === latest).length
      : 0;
    return `${fresh}/${denominator}`;
  }
  if (stepId === "intestacy") {
    const work = treesPipelineLaneWork("intestacy", tree);
    if (work) return formatInteger(work.eligible);
  }
  const mapped = stageForStep(stepId, health);
  if (mapped) return formatInteger(mapped.ready);
  return "unavailable";
}

function inspectorHtml(
  step: PipelineStep,
  workflow: PipelineWorkflowDocument,
  open: boolean,
  health: PropertiesConveyorHealth | null = null,
  preview: PipelineWorkflowPublishPreview | null | undefined = null
): string {
  const tools = step.tools
    .map((tool, index) => {
      const availability = pipelineToolAvailability(tool);
      const role = availability === "unavailable" || availability === "disabled"
        ? availability
        : index === 0 ? "Primary" : "Fallback";
      return `
      <div class="pipeline-tool${index === 0 && availability === "executable" ? " one" : ""}">
        <span class="n">${index + 1}</span>
        <div>
          <strong>${escapeHtml(toolLabel(tool))}</strong>
          <em>${escapeHtml(role)} · ${escapeHtml(tool)}</em>
        </div>
        <span class="pipeline-tool-spacer"></span>
        <button type="button" class="tiny" data-tool-move="${escapeAttr(step.id)}" data-from="${index}" data-to="${Math.max(0, index - 1)}">Up</button>
        <button type="button" class="tiny" data-tool-move="${escapeAttr(step.id)}" data-from="${index}" data-to="${Math.min(step.tools.length - 1, index + 1)}">Down</button>
      </div>`;
    })
    .join("");
  const unusedTools = REGISTERED_TOOL_IDS.filter((id) => !step.tools.includes(id));
  const gate = step.gate ?? {};
  const work = health?.tree ? treesPipelineLaneWork(step.id, health.tree) : null;
  return `
    <aside class="pipeline-drawer${open ? " is-on" : ""}" data-pipeline-inspector>
      <p class="muted">Step · ${escapeHtml(step.id)}</p>
      <label class="k">Title</label>
      <input type="text" maxlength="80" data-step-title value="${escapeAttr(step.title)}" />
      <div class="pipeline-row">
        <button type="button" class="tiny" data-step-move="-1">Earlier</button>
        <button type="button" class="tiny" data-step-move="1">Later</button>
        <button type="button" class="btn" data-step-remove>Remove step</button>
      </div>
      <p class="lead">Registered tools only. A miss does not call the next vendor inside the same tool. Lanes: ${escapeHtml(pipelineBoardLaneIds(workflow).join(" → "))}.</p>
      ${laneRulesHtml(step, workflow, work, health)}
      ${pipelineWorkflowPreviewHtml(preview)}
      ${tools || `<p class="muted">No tools on this step.</p>`}
      <label class="k">Add registered tool</label>
      <div class="pipeline-inline">
        <select data-add-tool ${unusedTools.length ? "" : "disabled"}>
          ${unusedTools.map((id) => {
            const availability = pipelineToolAvailability(id);
            const mark = availability === "unavailable" || availability === "disabled" ? ` · ${availability}` : "";
            return `<option value="${escapeAttr(id)}">${escapeHtml(toolLabel(id))} (${escapeHtml(id)}${escapeHtml(mark)})</option>`;
          }).join("")}
        </select>
        <button type="button" class="btn" data-add-tool-confirm ${unusedTools.length ? "" : "disabled"}>+ Add</button>
      </div>
      <label class="k">If every tool misses</label>
      <select data-step-miss>
        ${(["block", "hold"] as const).map((miss) => `<option value="${miss}"${step.miss === miss ? " selected" : ""}>${escapeHtml(MISS_LABELS[miss])}</option>`).join("")}
      </select>
      <label class="k">Auction window (days)</label>
      <input type="number" min="1" step="1" data-gate-auction-days value="${gate.auctionWindowDays ?? ""}" placeholder="30" />
      <label class="k">Date refresh cadence</label>
      <select data-gate-cadence>
        <option value=""${gate.dateRefreshCadence ? "" : " selected"}>Not set</option>
        <option value="weekly"${gate.dateRefreshCadence === "weekly" ? " selected" : ""}>Weekly</option>
        <option value="never"${gate.dateRefreshCadence === "never" ? " selected" : ""}>Never</option>
      </select>
      <label class="k pipeline-check">
        <input type="checkbox" data-gate-deceased ${gate.deceasedRequired ? "checked" : ""} />
        Deceased required
      </label>
      <label class="k pipeline-check">
        <input type="checkbox" data-gate-obituary ${gate.obituaryRequired ? "checked" : ""} />
        Obituary required
      </label>
      <label class="k">Equity minimum (USD)</label>
      <input type="number" min="0" step="1000" data-gate-equity-min value="${gate.equityMinUsd ?? ""}" placeholder="25000" />
      <label class="k">Market value percent</label>
      <input type="number" min="1" step="1" data-gate-value-pct value="${gate.marketValuePct ?? ""}" placeholder="80" />
      <label class="k">Loan value percent</label>
      <input type="number" min="1" step="1" data-gate-loan-pct value="${gate.loanValuePct ?? ""}" placeholder="115" />
      <p class="hint">Adding a brand-new vendor is code. After it is registered, place it in this list. The swimlane header updates from the same document. Save writes the document only; it does not rewrite properties.</p>
      <div class="pipeline-row">
        <button type="button" class="btn main" data-step-save>Save step</button>
        <button type="button" class="btn" data-drawer-close>Close</button>
      </div>
    </aside>
  `;
}

export function pipelineWorkflowPreviewHtml(
  preview: PipelineWorkflowPublishPreview | null | undefined
): string {
  if (!preview) return "";
  const listed = preview.identityKeys.map((key) => `<li>${escapeHtml(key)}</li>`).join("");
  return `
    <div class="hint" data-pipeline-workflow-preview data-preview-count="${escapeAttr(String(preview.count))}">
      <p><strong>Publish preview</strong></p>
      <p>${escapeHtml(String(preview.count))} ${preview.count === 1 ? "house" : "houses"} would change hop or wake-up.</p>
      ${listed ? `<ul>${listed}</ul>` : `<p class="muted">No houses would change hop or wake-up.</p>`}
    </div>
  `;
}

function addStepSheetHtml(workflow: PipelineWorkflowDocument, open: boolean): string {
  const lastId = workflow.steps.at(-1)?.id ?? "";
  return `
    <div class="pipeline-sheet${open ? " is-on" : ""}" data-add-sheet>
      <div class="pipeline-sheet-box">
        <h2>Add a step</h2>
        <p class="muted">This writes the same workflow document the runner uses. You can only attach tools that already exist.</p>
        <label class="k">Name</label>
        <input type="text" data-new-name maxlength="80" value="New step" />
        <label class="k">Kind</label>
        <select data-new-kind>
          ${STEP_KINDS.map((kind) => `<option value="${kind}"${kind === "enrichment" ? " selected" : ""}>${escapeHtml(kind)}</option>`).join("")}
        </select>
        <label class="k">Insert after</label>
        <select data-new-after>
          ${workflow.steps.map((step) => `<option value="${escapeAttr(step.id)}"${step.id === lastId ? " selected" : ""}>${escapeHtml(step.title)}</option>`).join("")}
        </select>
        <label class="k">Tool (already registered)</label>
        <select data-new-tool>
          ${REGISTERED_TOOL_IDS.map((id) => {
            const availability = pipelineToolAvailability(id);
            const mark = availability === "unavailable" || availability === "disabled" ? ` · ${availability}` : "";
            return `<option value="${escapeAttr(id)}">${escapeHtml(toolLabel(id))} (${escapeHtml(id)}${escapeHtml(mark)})</option>`;
          }).join("")}
        </select>
        <p class="hint">If the tool does not exist yet, that is the programming event. After it is registered, adding this lane is you — not a visualizer patch.</p>
        <div class="pipeline-row">
          <button type="button" class="btn main" data-confirm-add>Add to board</button>
          <button type="button" class="btn" data-cancel-add>Cancel</button>
        </div>
      </div>
    </div>
  `;
}

function stepChips(
  step: PipelineStep,
  tools: NonNullable<PropertiesConveyorHealth["tools"]> | undefined = undefined
): Array<{ kind: string; text: string }> {
  const chips: Array<{ kind: string; text: string }> = step.tools.map((tool, index) => {
    const availability = pipelineToolAvailability(tool, tools?.find((row) => row.toolId === tool));
    if (availability === "unavailable" || availability === "disabled") {
      const status = tools?.find((row) => row.toolId === tool);
      const label = status
        ? pipelineToolStateLabel(status.state, status.failureClass)
        : availability;
      return { kind: "bad", text: `${toolLabel(tool)} · ${label}` };
    }
    const status = tools?.find((row) => row.toolId === tool);
    if (status) {
      return {
        kind: pipelineToolStateChipClass(status.state),
        text: `${toolLabel(tool)} · ${pipelineToolStateLabel(status.state, status.failureClass)}`
      };
    }
    if (tools) {
      return {
        kind: pipelineToolStateChipClass("unknown"),
        text: `${toolLabel(tool)} · ${pipelineToolStateLabel("unknown", null)}`
      };
    }
    return { kind: index === 0 ? "one" : "", text: toolLabel(tool) };
  });
  const gate = step.gate;
  if (gate?.auctionWindowDays) chips.push({ kind: "wait", text: `> ${gate.auctionWindowDays} days` });
  if (gate?.dateRefreshCadence === "weekly") chips.push({ kind: "one", text: "weekly date" });
  if (gate?.deceasedRequired) chips.push({ kind: "one", text: "deceased" });
  if (gate?.obituaryRequired) chips.push({ kind: "one", text: "obituary" });
  if (gate?.equityMinUsd != null) chips.push({ kind: "one", text: `≥ $${formatInteger(gate.equityMinUsd)}` });
  if (gate?.marketValuePct != null) chips.push({ kind: "", text: `${gate.marketValuePct}% value` });
  if (gate?.loanValuePct != null) chips.push({ kind: "", text: `${gate.loanValuePct}% loan` });
  if (step.kind === "terminal") chips.push({ kind: step.miss === "block" ? "" : "one", text: "terminal" });
  return chips;
}

function stepCards(
  step: PipelineStep,
  tools: NonNullable<PropertiesConveyorHealth["tools"]> | undefined = undefined
): Array<{ title: string; note: string }> {
  const lane = pipelineStepLaneAvailability(step, tools);
  if (lane === "unavailable" || lane === "disabled") {
    const names = step.tools.map((id) => toolLabel(id)).join(", ") || "this step";
    return [{
      title: "Unavailable",
      note: lane === "disabled"
        ? `${names} is disabled. This is not an empty healthy lane.`
        : `${names} is unavailable. This is not an empty healthy lane.`
    }];
  }
  if (step.kind === "terminal" && step.tools.length === 0) {
    return [{ title: "Terminal sink", note: "Houses land here. This lane is not a vendor." }];
  }
  if (step.tools.length === 0) {
    return [{ title: "Unavailable", note: "No registered tool on this step. This is not an empty healthy lane." }];
  }
  const status = tools?.find((row) => row.toolId === (step.tools[0] ?? ""));
  const fallbackNote = step.tools.length > 1
    ? `${toolLabel(step.tools[0] ?? "")} then fallbacks. Order is explicit; unavailable fallbacks stay marked unavailable.`
    : "Primary · no fallback yet";
  const note = typeof status?.message === "string" && status.message.trim()
    ? status.message.trim()
    : fallbackNote;
  return [
    {
      title: step.tools.length > 1 ? "Atomic tools" : toolLabel(step.tools[0] ?? ""),
      note
    }
  ];
}

function ingestStuckStage(
  health: PropertiesConveyorHealth | null
): { id: string; ready: number; deadends: Array<{ class: string; count: number }> } | null {
  if (!health?.tree?.sources?.length) return null;
  const deadends = ingestTreeStatuses(health, ingestAsOf(health))
    .filter((row) => row.stale || row.missing)
    .map((row) => ({
      class: row.missing ? `ingest_missing_${row.id}` : `ingest_stale_${row.id}`,
      count: 1
    }));
  if (deadends.length === 0) return null;
  return { id: "ingest", ready: 0, deadends };
}

function deadendsForLane(
  stepId: string,
  deadends: Array<{ class: string; count: number }>
): Array<{ class: string; count: number }> {
  if (stepId === "waiting") return deadends.filter((row) => row.class === "over_30_days");
  if (stepId === "closed") {
    return deadends.filter((row) => row.class === "sale_cancelled" || row.class === "sale_complete" || row.class === "invalid_owner_name");
  }
  if (stepId === "auction_window") {
    return deadends.filter((row) => row.class !== "over_30_days" && row.class !== "sale_cancelled" && row.class !== "sale_complete" && row.class !== "invalid_owner_name");
  }
  if (stepId === "value" || stepId === "plv" || stepId === "equity") {
    return deadends.filter((row) => valueLaneForDeadend(row.class) === stepId);
  }
  if (stepId === "obit" || stepId === "intestacy") {
    return deadends.filter((row) => obitLaneForDeadend(row.class) === stepId);
  }
  return deadends;
}

function valueLaneForDeadend(failureClass: string): "value" | "plv" | "equity" {
  if (failureClass === "blocked_no_plv" || failureClass.startsWith("plv_") || (failureClass.startsWith("ecourts_sp_plv") && failureClass.endsWith("_miss"))) return "plv";
  if (failureClass === "blocked_no_equity" || failureClass.includes("equity")) return "equity";
  return "value";
}

function obitLaneForDeadend(failureClass: string): "obit" | "intestacy" {
  if (failureClass.includes("intestacy")) return "intestacy";
  return "obit";
}

function deadendCopy(failureClass: string): string {
  const named = TREES_PIPELINE_DEADEND_COPY[failureClass];
  if (named) return named;
  const ingest = /^(ingest_stale|ingest_missing)_(.+)$/.exec(failureClass);
  if (ingest) {
    const treeId = ingest[2] as keyof typeof TREE_INGEST_LABELS;
    const label = TREE_INGEST_LABELS[treeId] ?? labelize(ingest[2] ?? "");
    return ingest[1] === "ingest_missing" ? `${label} missing from D1.` : `${label} scrape is stale.`;
  }
  return labelize(failureClass);
}

function toolLabel(id: string): string {
  return pipelineToolTitle(id);
}

function healthBadge(health: PropertiesConveyorHealth | null, ingestStalled = false): string {
  if (ingestStalled) return `<span class="chip bad">ingest stalled</span>`;
  if (!health?.tree) return `<span class="chip">counts unavailable</span>`;
  if (health.persistError) return `<span class="chip bad">health persist failed</span>`;
  if (health.snapshotStatus === "cached") return `<span class="chip wait">cached health</span>`;
  const generated = health.generatedAt ? new Date(health.generatedAt) : null;
  const when = generated && !Number.isNaN(generated.getTime()) ? generated.toLocaleString() : "";
  return `<span class="chip one">${escapeHtml(when ? `VPS · SQLite · ${when}` : "VPS · SQLite")}</span>`;
}

function paintPipelineBoard(mount: HTMLElement, state: BoardState): void {
  rememberPipelineBoardScroll(mount, state);
  mount.innerHTML = `${pipelineBoardHtml(state.workflow, state.health, state.selectedId, {
    drawerOpen: state.drawerOpen,
    sheetOpen: state.sheetOpen,
    workflowError: state.workflowError,
    workflowPreview: state.workflowPreview
  })}${pipelineRunInspectorHtml(state)}`;
  bindPipelineBoard(mount, state);
  restorePipelineBoardScroll(mount, state);
}

function bindEcourtsMonitor(mount: HTMLElement): void {
  const monitor = mount.querySelector("[data-ecourts-monitor]");
  if (!(monitor instanceof HTMLDetailsElement)) return;
  monitor.addEventListener("toggle", () => persistEcourtsMonitorOpen(monitor.open));
}

function bindPipelineBoard(mount: HTMLElement, state: BoardState): void {
  bindEcourtsMonitor(mount);
  const lanes = mount.querySelector("[data-pipeline-lanes]");
  if (lanes) {
    lanes.addEventListener("scroll", () => {
      state.laneScrollLeft = lanes.scrollLeft;
    }, { passive: true });
  }
  const paint = (patch: Partial<BoardState> = {}): void => {
    Object.assign(state, patch);
    paintPipelineBoard(mount, state);
  };
  mount.querySelectorAll("[data-pipeline-step]").forEach((node) => {
    node.addEventListener("click", () => {
      const id = (node as HTMLElement).getAttribute("data-pipeline-step") ?? "";
      paint({ selectedId: id, drawerOpen: true, sheetOpen: false });
    });
  });
  mount.querySelectorAll("[data-pipeline-add]").forEach((node) => {
    node.addEventListener("click", () => paint({ sheetOpen: true }));
  });
  mount.querySelector("[data-pipeline-edit]")?.addEventListener("click", () => paint({ drawerOpen: true }));
  mount.querySelector("[data-drawer-close]")?.addEventListener("click", () => paint({ drawerOpen: false }));
  mount.querySelector("[data-cancel-add]")?.addEventListener("click", () => paint({ sheetOpen: false }));
  const commit = (next: PipelineWorkflowDocument, extra: Partial<BoardState> = {}): void => {
    void commitPipelineWorkflow(state, next, paint, extra);
  };
  const applyLocal = (build: () => PipelineWorkflowDocument, extra: Partial<BoardState> = {}): void => {
    try {
      commit(build(), extra);
    } catch (error) {
      paint({
        workflowError: {
          ok: false,
          failureClass: "invalid_pipeline_workflow",
          message: error instanceof Error ? error.message : "invalid_pipeline_workflow"
        },
        ...extra
      });
    }
  };
  mount.querySelectorAll("[data-tool-move]").forEach((node) => {
    node.addEventListener("click", (event) => {
      event.stopPropagation();
      const stepId = (node as HTMLElement).getAttribute("data-tool-move") ?? "";
      const from = Number((node as HTMLElement).getAttribute("data-from"));
      const to = Number((node as HTMLElement).getAttribute("data-to"));
      applyLocal(() => movePipelineTool(state.workflow, stepId, from, to), { selectedId: stepId, drawerOpen: true });
    });
  });
  mount.querySelector("[data-add-tool-confirm]")?.addEventListener("click", () => {
    const select = mount.querySelector<HTMLSelectElement>("[data-add-tool]");
    const tool = select?.value ?? "";
    if (!isRegisteredToolId(tool)) return;
    const step = state.workflow.steps.find((item) => item.id === state.selectedId);
    if (!step || step.tools.includes(tool)) return;
    applyLocal(() => replaceStep(state.workflow, { ...step, tools: [...step.tools, tool] }), { drawerOpen: true });
  });
  mount.querySelectorAll("[data-step-move]").forEach((node) => {
    node.addEventListener("click", (event) => {
      event.stopPropagation();
      const stepId = state.selectedId;
      const from = state.workflow.steps.findIndex((item) => item.id === stepId);
      if (from < 0) return;
      const delta = Number((node as HTMLElement).getAttribute("data-step-move"));
      applyLocal(() => movePipelineStep(state.workflow, stepId, from + delta), { selectedId: stepId, drawerOpen: true });
    });
  });
  mount.querySelector("[data-step-remove]")?.addEventListener("click", (event) => {
    event.stopPropagation();
    const stepId = state.selectedId;
    const index = state.workflow.steps.findIndex((item) => item.id === stepId);
    const fallback = state.workflow.steps[index - 1] ?? state.workflow.steps[index + 1];
    applyLocal(() => removePipelineStep(state.workflow, stepId), {
      selectedId: fallback?.id ?? "",
      drawerOpen: Boolean(fallback)
    });
  });
  mount.querySelector("[data-step-save]")?.addEventListener("click", () => {
    const step = state.workflow.steps.find((item) => item.id === state.selectedId);
    if (!step) return;
    applyLocal(() => replaceStep(state.workflow, readInspectorStep(mount, step)), { drawerOpen: true });
  });
  mount.querySelector("[data-confirm-add]")?.addEventListener("click", () => {
    const afterId = mount.querySelector<HTMLSelectElement>("[data-new-after]")?.value ?? "";
    const title = mount.querySelector<HTMLInputElement>("[data-new-name]")?.value.trim() || "New step";
    const kindValue = mount.querySelector<HTMLSelectElement>("[data-new-kind]")?.value ?? "enrichment";
    const tool = mount.querySelector<HTMLSelectElement>("[data-new-tool]")?.value ?? "";
    if (!isRegisteredToolId(tool)) return;
    const kind: PipelineStepKind = (STEP_KINDS as readonly string[]).includes(kindValue)
      ? (kindValue as PipelineStepKind)
      : "enrichment";
    try {
      const next = addPipelineStep(state.workflow, {
        afterId,
        step: {
          id: uniqueStepId(title, state.workflow),
          title,
          kind,
          tools: [tool],
          miss: "hold"
        }
      });
      const added = next.steps.find((step) => !state.workflow.steps.some((prior) => prior.id === step.id));
      commit(next, { selectedId: added?.id ?? afterId, drawerOpen: true, sheetOpen: false });
    } catch (error) {
      paint({
        sheetOpen: true,
        workflowError: {
          ok: false,
          failureClass: "invalid_pipeline_workflow",
          message: error instanceof Error ? error.message : "invalid_pipeline_workflow"
        }
      });
    }
  });
  bindNewTicketControl(mount);
  bindPipelineRunInspector(mount, state, paint);
}

function readInspectorStep(mount: HTMLElement, step: PipelineStep): PipelineStep {
  const title = mount.querySelector<HTMLInputElement>("[data-step-title]")?.value.trim() || step.title;
  const missValue = mount.querySelector<HTMLSelectElement>("[data-step-miss]")?.value;
  const miss: PipelineMissBehavior = missValue === "hold" ? "hold" : "block";
  const auctionWindowDays = readPositiveInt(mount.querySelector<HTMLInputElement>("[data-gate-auction-days]")?.value);
  const cadence = mount.querySelector<HTMLSelectElement>("[data-gate-cadence]")?.value;
  const deceasedRequired = Boolean(mount.querySelector<HTMLInputElement>("[data-gate-deceased]")?.checked);
  const obituaryRequired = Boolean(mount.querySelector<HTMLInputElement>("[data-gate-obituary]")?.checked);
  const equityMinUsd = readNumber(mount.querySelector<HTMLInputElement>("[data-gate-equity-min]")?.value);
  const marketValuePct = readPositiveInt(mount.querySelector<HTMLInputElement>("[data-gate-value-pct]")?.value);
  const loanValuePct = readPositiveInt(mount.querySelector<HTMLInputElement>("[data-gate-loan-pct]")?.value);
  const gate: NonNullable<PipelineStep["gate"]> = { deceasedRequired, obituaryRequired };
  if (auctionWindowDays != null) gate.auctionWindowDays = auctionWindowDays;
  if (cadence === "weekly" || cadence === "never") gate.dateRefreshCadence = cadence;
  if (equityMinUsd != null) gate.equityMinUsd = equityMinUsd;
  if (marketValuePct != null) gate.marketValuePct = marketValuePct;
  if (loanValuePct != null) gate.loanValuePct = loanValuePct;
  return { ...step, title, miss, gate };
}

function replaceStep(workflow: PipelineWorkflowDocument, step: PipelineStep): PipelineWorkflowDocument {
  return parsePipelineWorkflow({
    ...workflow,
    steps: workflow.steps.map((item) => (item.id === step.id ? step : item))
  });
}

function uniqueStepId(title: string, workflow: PipelineWorkflowDocument): string {
  const base = title.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 72) || "step";
  const used = new Set(workflow.steps.map((step) => step.id));
  if (!used.has(base)) return base;
  let n = 2;
  while (used.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

function readPositiveInt(value: string | undefined): number | undefined {
  if (!value?.trim()) return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return Math.trunc(parsed);
}

function readNumber(value: string | undefined): number | undefined {
  if (!value?.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function workflowErrorFromHealth(
  health: PropertiesConveyorHealth | null
): Extract<PipelineWorkflowSaveResult, { ok: false }> | null {
  if (health?.workflowFailureClass !== "pipeline_workflow_load_failed") return null;
  return {
    ok: false,
    failureClass: "pipeline_workflow_load_failed",
    message: pipelineWorkflowSaveFailureMessage("pipeline_workflow_load_failed")
  };
}

export function pipelineWorkflowErrorHtml(
  error: Extract<PipelineWorkflowSaveResult, { ok: false }> | null | undefined
): string {
  if (!error) return "";
  const load = error.failureClass === "pipeline_workflow_load_failed";
  return `
    <aside class="pipeline-attention" data-pipeline-workflow-error="${escapeAttr(error.failureClass)}" role="alert">
      <p class="pipeline-attention-kicker">${load ? "Load failed" : "Save failed"}</p>
      <h2>${escapeHtml(pipelineWorkflowSaveFailureMessage(error.failureClass))}</h2>
      <p>${escapeHtml(error.message)}</p>
    </aside>
  `;
}

async function commitPipelineWorkflow(
  state: BoardState,
  next: PipelineWorkflowDocument,
  paint: (patch: Partial<BoardState>) => void,
  extra: Partial<BoardState> = {}
): Promise<void> {
  if (state.saving) return;
  state.saving = true;
  try {
    const preview = await previewPipelineWorkflow(next);
    const result = await persistPipelineWorkflow(next, state.revision);
    if (result.ok) {
      paint({
        workflow: result.record.document,
        revision: result.record.revision,
        workflowError: null,
        workflowPreview: preview,
        ...extra
      });
      return;
    }
    paint({
      workflowError: result,
      workflowPreview: preview,
      ...(result.record ? { workflow: result.record.document, revision: result.record.revision } : {}),
      ...extra
    });
  } finally {
    state.saving = false;
  }
}

async function loadPipelineWorkflow(): Promise<{
  document: PipelineWorkflowDocument;
  revision: number;
  error: Extract<PipelineWorkflowSaveResult, { ok: false }> | null;
}> {
  try {
    const { authenticatedFetch } = await import("./dashboard-auth.js");
    const api = await authenticatedFetch(PIPELINE_WORKFLOW_PATH);
    if (api.ok) {
      const record = parsePipelineWorkflowRecord(await api.json());
      return { document: record.document, revision: record.revision, error: null };
    }
    const result = pipelineWorkflowSaveResultFromHttp(
      api.status,
      await api.json().catch(() => null),
      "pipeline_workflow_load_failed"
    );
    return {
      document: result.record?.document ?? defaultPipelineWorkflow,
      revision: result.record?.revision ?? 0,
      error: result.ok ? null : result
    };
  } catch {
    return {
      document: defaultPipelineWorkflow,
      revision: 0,
      error: {
        ok: false,
        failureClass: "pipeline_workflow_load_failed",
        message: pipelineWorkflowSaveFailureMessage("pipeline_workflow_load_failed")
      }
    };
  }
}

async function previewPipelineWorkflow(
  document: PipelineWorkflowDocument
): Promise<PipelineWorkflowPublishPreview | null> {
  try {
    const { authenticatedFetch } = await import("./dashboard-auth.js");
    const response = await authenticatedFetch(PIPELINE_WORKFLOW_PREVIEW_PATH, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(document)
    });
    const body = await response.json().catch(() => null);
    if (!response.ok || !body || typeof body !== "object" || Array.isArray(body)) return null;
    const rec = body as { identityKeys?: unknown; count?: unknown };
    if (!Array.isArray(rec.identityKeys)) return null;
    const identityKeys = rec.identityKeys.map((key) => String(key));
    const count = typeof rec.count === "number" && Number.isFinite(rec.count) ? rec.count : identityKeys.length;
    return { identityKeys, count };
  } catch {
    return null;
  }
}

async function persistPipelineWorkflow(
  document: PipelineWorkflowDocument,
  expectedRevision: number
): Promise<PipelineWorkflowSaveResult> {
  try {
    const { authenticatedFetch } = await import("./dashboard-auth.js");
    const response = await authenticatedFetch(PIPELINE_WORKFLOW_PATH, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ...document, expectedRevision })
    });
    const body = await response.json().catch(() => null);
    return pipelineWorkflowSaveResultFromHttp(response.status, body);
  } catch {
    return {
      ok: false,
      failureClass: "pipeline_workflow_network_failure",
      message: pipelineWorkflowSaveFailureMessage("pipeline_workflow_network_failure")
    };
  }
}
